import { Star } from 'lucide-react'

interface EffectivenessRatingProps {
  value: number | null
  onChange?: (rating: number | null) => void
  readonly?: boolean
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
}

/**
 * Effectiveness rating labels (1-5 scale)
 */
export const EFFECTIVENESS_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Not Effective', color: 'text-red-500' },
  2: { label: 'Partially Effective', color: 'text-orange-500' },
  3: { label: 'Moderately Effective', color: 'text-yellow-500' },
  4: { label: 'Effective', color: 'text-lime-500' },
  5: { label: 'Highly Effective', color: 'text-green-500' },
}

/**
 * EffectivenessRating Component
 *
 * A star-based rating component for control effectiveness (1-5 scale).
 * - Hover to preview rating
 * - Click to set rating
 * - Click active star to clear rating
 */
export default function EffectivenessRating({
  value,
  onChange,
  readonly = false,
  size = 'md',
  showLabel = true,
}: EffectivenessRatingProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  }

  const handleClick = (rating: number) => {
    if (readonly || !onChange) return

    // If clicking the current rating, clear it
    if (value === rating) {
      onChange(null)
    } else {
      onChange(rating)
    }
  }

  const effectivenessInfo = value ? EFFECTIVENESS_LABELS[value] : null

  return (
    <div className="inline-flex flex-col">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleClick(star)}
            disabled={readonly}
            className={`${
              readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'
            } focus:outline-none`}
            title={EFFECTIVENESS_LABELS[star].label}
          >
            <Star
              className={`${sizeClasses[size]} ${
                value && star <= value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>

      {showLabel && effectivenessInfo && (
        <span className={`text-xs mt-0.5 ${effectivenessInfo.color}`}>
          {effectivenessInfo.label}
        </span>
      )}

      {showLabel && !value && !readonly && (
        <span className="text-xs mt-0.5 text-gray-400">
          Click to rate
        </span>
      )}
    </div>
  )
}

/**
 * EffectivenessRatingDisplay Component
 *
 * A simpler read-only display for effectiveness rating in tables/lists.
 */
export function EffectivenessRatingDisplay({
  value,
  size = 'sm',
}: {
  value: number | null
  size?: 'sm' | 'md'
}) {
  if (!value) {
    return <span className="text-gray-400 text-xs">Not rated</span>
  }

  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
  }

  const info = EFFECTIVENESS_LABELS[value]

  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= value
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-200'
            }`}
          />
        ))}
      </div>
      <span className={`text-xs ${info.color}`}>{info.label}</span>
    </div>
  )
}
